import { getBootSnapshot, recoverBoot, captureBootSignal } from '../root/boot-budget.js';
import { ElectronAPI } from '#~/electron/index.js';
// ENG-4154: read the IdentityState store, not a per-consumer bridge poll.
import { resolveLinkedAgentId } from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import { config } from '#~/config/index.js';
import { post } from '#~/notifications/utils.js';
import { shouldAutoLink } from '#~/routes/verify-auto-link/v2/auto-link-vendor.js';
import { Button } from '@studio/design-system/button';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import type { Agent } from '@studio/models/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useEffect } from 'react';
import type { ReactElement } from 'react';
import * as persistentLayer from '#~/persistence/index.js';
import { msSinceLastBootProgress, noteBootProgress } from '#~/logger/forward.js';
import { withTimeout } from '#~/lib/with-timeout.js';
import type { BridgeLivenessSnapshot } from '#~/electron/api.js';

let timeout: NodeJS.Timeout;
let didRun = false;

let showingMessage = false;

// A bridge that is demonstrably ALIVE but not yet connected to the studio is
// starting, not wedged. Relaunching restarts that startup from zero, so on a
// slow machine (AV scanning freshly installed binaries) the reboot makes the
// wait longer and then posts "could not start" during the second boot. While
// the bridge is alive keep the boot-progress clock fresh — which also holds
// off the BootOverlay's 45s stall recovery — and re-arm. Bounded, so a bridge
// that is alive yet never connects still reaches the recovery paths below.
export const BRIDGE_ALIVE_STARTUP_CEILING_MS = 120_000;
// The auto-restart throttle: a soft reset stamped less than this long ago
// means the app is mid-reboot, so post the failure toast instead of another
// relaunch. 60s predates the alive gate and is right for a dead bridge, whose
// decision lands one watchdog window after mount.
const AUTO_RESET_COOLDOWN_MS = 60_000;
// After a full alive hold the decision lands >= the ceiling after mount, so a
// 60s throttle can never be "mid-reboot" and an alive-but-never-connecting
// bridge (encoder crash-looping under kor, LiveKit blocked) would relaunch
// every ceiling forever with the toast unreachable. Widen the throttle past
// ceiling + relaunch + boot for that path: one recovery relaunch, then the
// toast the next session. Exported for tests.
export const ALIVE_HOLD_RESET_COOLDOWN_MS = 10 * 60_000;
// The snapshot never touches the sidecar (a port-file stat and a latch read),
// so the bound only protects the watchdog from a hung IPC channel — it must
// not be what decides "alive" on a slow machine.
const BRIDGE_LIVENESS_PROBE_TIMEOUT_MS = 3_000;

/**
 * Is the local bridge alive? The kor process main spawned is the authority
 * when main can report it: kor owns every bridge service and restarts them,
 * so "kor running" is "the bridge is starting or running" and "kor gone" is
 * a real failure even if a port file or ready latch from earlier survives.
 * Without that report, fall back to the sidecar signals: its port file (it is
 * up, even if not yet answering) or a recorded answer this session.
 * Pure + exported for unit tests.
 */
export const isBridgeAlive = (liveness: BridgeLivenessSnapshot | undefined): boolean => {
  if (liveness === undefined) return false;
  if (typeof liveness.korAlive === 'boolean') return liveness.korAlive;
  return liveness.authReady || liveness.launcherReady;
};

/**
 * Should the watchdog keep waiting instead of rebooting or posting a failure?
 * Yes while the bridge is alive and the wait is still inside the startup
 * ceiling. Pure + exported for unit tests.
 */
export const shouldWaitForAliveBridge = (
  liveness: BridgeLivenessSnapshot | undefined,
  waitedMs: number,
  ceilingMs: number = BRIDGE_ALIVE_STARTUP_CEILING_MS
): boolean => waitedMs < ceilingMs && isBridgeAlive(liveness);

/**
 * May the watchdog auto-relaunch? Yes when no soft reset is recorded (or the
 * record is unreadable), or the last one is older than the cooldown. The
 * stamp is SECONDS since the epoch, persisted across app.relaunch().
 * Pure + exported for unit tests.
 */
export const shouldAutoReset = (
  lastResetStamp: string | null | undefined,
  nowMs: number,
  cooldownMs: number
): boolean => {
  if (lastResetStamp === undefined || lastResetStamp === null || lastResetStamp === '') {
    return true;
  }
  const lastResetSeconds = Number(lastResetStamp);
  if (Number.isNaN(lastResetSeconds)) {
    return true;
  }
  return nowMs / 1000 - lastResetSeconds > cooldownMs / 1000;
};

const probeBridgeLiveness = async (): Promise<BridgeLivenessSnapshot | undefined> => {
  try {
    return await withTimeout(
      ElectronAPI.bridgeLiveness(),
      BRIDGE_LIVENESS_PROBE_TIMEOUT_MS,
      'bridge:liveness'
    );
  } catch {
    // A failed or hung probe is "not proven alive" — fall through to the
    // existing recovery decision rather than suppressing it on no evidence.
    return undefined;
  }
};

/**
 * Hive and unresolved vendors intentionally remain unlinked until manual
 * pairing. Route that steady state through the same fail-closed vendor decision
 * as Auto Link so the watchdog cannot reinterpret it as a boot failure.
 * See knowledge: hive-no-auto-link-bootstrap-contract
 * (ai.knowledge/platform); per rule: think-in-systems (ai.knowledge/global).
 */
const isExpectedUnlinkedManualPairState = (linkedAgentId: Agent['id'] | undefined): boolean =>
  isElectronApp('interface') &&
  linkedAgentId === undefined &&
  !shouldAutoLink({ isElectron: true, vendor: config?.vendor });

// Symptom #6 — bridge-connect popup honesty. The "could not start" toast only
// reaches its branch when `canAutoReset()` is false, i.e. a soft reset landed
// <60s ago: the app is MID-REBOOT, not genuinely wedged. Showing "Try Again"
// during that in-session reboot teardown is a false positive (it fires even on
// fast machines). Suppress it until the boot has had an extended grace and
// still failed. `iris-loaded-at` is stored in SECONDS in sessionStorage, which
// is reset on every app.relaunch(), so this measures the CURRENT session's boot
// age — sufficient for suppression, since a freshly-relaunched session's
// timestamp is recent and the gate holds through the reboot-teardown window.
const EXTENDED_BOOT_GRACE_MS = 30_000;

/** `<AgentLookup>` route. */
export const TimeoutWatchdog = (props: {
  readonly agentIDs: Agent['id'][];
  readonly timeout?: T_Maybe<number>;
  readonly enabled?: boolean;
}): ReactElement => {
  const enabled = props.enabled ?? true;
  const isOnline = () => navigator.onLine;

  const canAutoReset = async (cooldownMs: number = AUTO_RESET_COOLDOWN_MS) =>
    shouldAutoReset(await persistentLayer.get('iris-reset-at'), Date.now(), cooldownMs);

  // `shouldProceed` is re-checked around the persistence await so a watchdog
  // that unmounted while stamping (the boot finished) never relaunches the
  // healthy app; user-driven callers (the toast buttons) pass nothing.
  const restart = async (
    shouldProceed: () => boolean = () => true,
    cooldownMs = AUTO_RESET_COOLDOWN_MS
  ) => {
    if (!shouldProceed()) {
      return;
    }
    if (getBootSnapshot()) {
      await recoverBoot('restart', cooldownMs);
      return;
    }
    const currentTime = +new Date() / 1000;
    await persistentLayer.set('iris-reset-at', currentTime.toString());
    if (!shouldProceed()) {
      return;
    }

    ElectronAPI.restart();
  };

  // listen to internet connection changes if it goes from offline to online
  useEffect(() => {
    // ENG-4052 gate first: a disabled watchdog installs no listener at all.
    if (!enabled) {
      return;
    }

    const launchSignal = captureBootSignal();
    let disposed = false;
    const fn = async () => {
      if (disposed || launchSignal.aborted) return;
      if (!isElectronApp('interface') || !showingMessage) {
        return;
      }

      // S7 (GAP2 / F2 completeness): the network just returned. The original
      // intent is "retry boot once the user reconnects", but this handler had
      // NO forward-progress / linked gate and `showingMessage` is never reset —
      // so a transient offline→online flap rebooted an app that had ALREADY
      // linked or was actively advancing (the reboot path the CI logs actually
      // hit, post-link). Only retry-restart if the boot is genuinely STILL
      // wedged: not linked AND not advancing. If it recovered, clear the sticky
      // flag so a later flap can't reboot a healthy app either.
      // Per rule: think-in-systems (ai.knowledge/global) — reboot on a real
      // failure signal, not on a normal network event.
      const watchdogDelay = props.timeout ?? 10_000;
      const stallMs = msSinceLastBootProgress();
      const bootAdvancing = stallMs !== null && stallMs < watchdogDelay;
      const linkedId = await resolveLinkedAgentId();
      if (disposed || launchSignal.aborted) return;
      if (linkedId || bootAdvancing || isExpectedUnlinkedManualPairState(linkedId)) {
        showingMessage = false;
        return;
      }

      await restart(() => !disposed && !launchSignal.aborted);
    };

    window.addEventListener('online', fn);

    return () => {
      disposed = true;
      window.removeEventListener('online', fn);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    didRun = false;
    const watchdogDelay = props.timeout ?? 10_000;
    // When this wait began: the bridge-alive gate below is bounded from here.
    const armedAt = Date.now();
    // Set by the cleanup. `fn` awaits IPC mid-flight, so an unmount (the room
    // navigated, the boot finished) can land between an await and the re-arm
    // below; without this flag that re-arm would install a fresh timer after
    // the cleanup cleared the old one and keep the watchdog running — and
    // eventually restarting — a healthy app from a route that no longer exists.
    const launchSignal = captureBootSignal();
    let cancelled = launchSignal.aborted;
    // Did the alive gate hold at least once this wait? Selects the wider
    // relaunch throttle (ALIVE_HOLD_RESET_COOLDOWN_MS) once the ceiling passes.
    let heldForAliveBridge = false;

    const fn = async () => {
      if (didRun || cancelled || launchSignal.aborted) {
        return;
      }

      didRun = true;

      clearTimeout(timeout);

      // F2 (report §11.1): require a POSITIVE failure signal before any reboot.
      // If a boot-progress mark landed within the last watchdog window the boot
      // is still advancing — a healthy-but-slow boot (e.g. a delayed auth
      // sidecar), NOT a wedge — so re-arm and re-check instead of rebooting.
      // The old path rebooted on bare elapsed time + the 60s canAutoReset
      // throttle, looping the reload every cycle (`iris-loaded-at` is
      // sessionStorage — it survives each reload). A boot that has STOPPED
      // advancing (stale mark) or never marked progress (null) falls through to
      // the existing recovery paths below, so a true wedge still recovers.
      // This gate sits before BOTH reboot branches (identity-gated + the
      // identity-agnostic fallback) so each honors forward progress.
      // Per rule: think-in-systems (ai.knowledge/global) — reboot follows a real
      // failure signal, not a timer side effect.
      const stallMs = msSinceLastBootProgress();
      const bootAdvancing = stallMs !== null && stallMs < watchdogDelay;
      if (isOnline() && bootAdvancing) {
        didRun = false;
        timeout = setTimeout(fn, watchdogDelay);
        return;
      }

      // Bridge-alive gate (see BRIDGE_ALIVE_STARTUP_CEILING_MS): a live sidecar
      // that has not connected yet is a slow start, not a wedge. Mark progress
      // so the overlay's stall recovery agrees, then re-arm. Sits before EVERY
      // reboot/toast branch below; the ceiling keeps it from suppressing them
      // forever. Per rule: think-in-systems (ai.knowledge/global) — the reboot
      // needs a positive failure signal, and "alive but slow" is not one.
      const liveness = isOnline() ? await probeBridgeLiveness() : undefined;
      if (cancelled || launchSignal.aborted) {
        return;
      }
      if (isOnline() && shouldWaitForAliveBridge(liveness, Date.now() - armedAt)) {
        heldForAliveBridge = true;
        noteBootProgress();
        didRun = false;
        timeout = setTimeout(fn, watchdogDelay);
        return;
      }

      const id = await resolveLinkedAgentId();
      if (cancelled || launchSignal.aborted) {
        return;
      }

      // One throttle read for both reboot branches below. Read AFTER the
      // cancellation check and checked again after: this is an IPC round-trip
      // (first `iris-reset-at` read of a session is always a cache miss) and
      // the boot can finish inside it.
      const resetAllowed = await canAutoReset(
        heldForAliveBridge ? ALIVE_HOLD_RESET_COOLDOWN_MS : AUTO_RESET_COOLDOWN_MS
      );
      if (cancelled || launchSignal.aborted) {
        return;
      }

      const initialLoad = window.sessionStorage.getItem('iris-loaded-at');

      // ENG-4154: gate this recovery restart on the same 60s `canAutoReset`
      // window as the unknown-error branch. Pre-migration this branch was
      // effectively inert mid-boot (the broken identity path returned
      // `undefined` while booting); with identity now resolving correctly, an
      // ungated restart here fires DURING a healthy-but-slow boot (e.g. a
      // delayed auth sidecar) and loops the reload every watchdog cycle —
      // `iris-loaded-at` is sessionStorage, so it survives each reload (CI
      // run 27302217500, boot-overlay bridge-offline on Windows).
      if (isOnline() && isElectronApp('interface') && id && initialLoad && resetAllowed) {
        restart(
          () => !cancelled && !launchSignal.aborted,
          heldForAliveBridge ? ALIVE_HOLD_RESET_COOLDOWN_MS : AUTO_RESET_COOLDOWN_MS
        );
        return;
      }

      if (!isOnline()) {
        showingMessage = true;
        post(
          {
            actionable: true,
            title: `${vendorName()} is Offline`,
            message: (
              <div>
                <span className='text-[12px]'>
                  In order to boot, {vendorName()} requires a connection to the internet. Please
                  check your connection and try again.
                </span>
                <div className='mb-0 flex flex-row gap-8'>
                  <Button
                    variant='link'
                    className='px-0 pt-12 text-primary no-underline'
                    onClick={() => (getBootSnapshot() ? void recoverBoot('retry') : void restart())}
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            ),
            severity: 'error'
          },
          { autoClose: false, closeable: true }
        );

        return;
      }

      if (isExpectedUnlinkedManualPairState(id)) {
        return;
      }

      if (resetAllowed) {
        await restart(
          () => !cancelled && !launchSignal.aborted,
          heldForAliveBridge ? ALIVE_HOLD_RESET_COOLDOWN_MS : AUTO_RESET_COOLDOWN_MS
        );
        return;
      }

      // Symptom #6: suppress the "could not start" toast during the boot/reboot
      // window. Reaching here means the throttle refused — a reset landed
      // inside the cooldown, so we are mid-reboot (or, after an alive hold,
      // the previous session already relaunched for the same alive-but-silent
      // bridge and it did not help). Only surface the toast once boot has
      // had an extended grace and still failed; otherwise the in-session reboot
      // teardown shows a false "Try Again" popup. Stay-in-scope: gates the
      // TOAST only; restart / canAutoReset logic is unchanged.
      const bootAgeMs =
        initialLoad && !Number.isNaN(Number(initialLoad))
          ? (+new Date() / 1000 - Number(initialLoad)) * 1000
          : Number.POSITIVE_INFINITY;
      if (bootAgeMs < EXTENDED_BOOT_GRACE_MS) {
        return;
      }

      showingMessage = true;
      post(
        {
          actionable: true,
          title: `${vendorName()} could not start`,
          message: (
            <div>
              <span className='text-[12px]'>
                An unknown error occurred that prevented {vendorName()} from starting. Please try
                again.
              </span>
              <div className='mb-0 flex flex-row gap-8'>
                <Button
                  variant='link'
                  className='px-0 pt-12 text-primary no-underline'
                  onClick={() => (getBootSnapshot() ? void recoverBoot('retry') : void restart())}
                >
                  Try Again
                </Button>
              </div>
            </div>
          ),
          severity: 'error'
        },
        { autoClose: false, closeable: true }
      );
    };

    timeout = setTimeout(fn, watchdogDelay);

    if (!isOnline()) {
      fn();
    }

    // listen for online status changes
    window.addEventListener('online', fn);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      window.removeEventListener('online', fn);
    };
  }, [enabled, props.timeout]);

  return <div />;
};
